import { useState, useContext } from "react";
import classNames from "classnames";
import { Image, Segment, Button, Flex, Icon } from "@simplybusiness/mobius";
import { plus, pencil, bin } from "@simplybusiness/icons";
import { ProductSummary } from "../../../shared/PersistentSummary";
import type { ProductProps } from "../../../shared/PersistentSummary/ProductSummary";
import { CombinedPaymentContext } from "../../../shared/PersistentSummary/CombinedPaymentContext";
import { fetchRequest } from "../../../shared/PersistentSummary/UtilsHelper";
import { DialogueModal } from "../../../shared/PersistentSummary/DialogueModal";
import type { SummaryWrapperProps, CartUpdateResponse } from "./types";

export const SummaryWrapper = ({
  productName = "",
  productSummary,
  totalGrossPremium,
  hasSharedLimits,
  insurerLogo,
  policyStartDate,
  mobileView,
  editURL,
  markedForPurchase,
  onUpdateItem,
  cartItemReference,
  promptInformation = { promptCustomer: false },
  eligibleForRemoval,
  editModalDescription,
  applicationDashboard = false,
  updateApplicationDashboard,
  isOnlyProduct,
  hasPrimaryCover,
}: SummaryWrapperProps) => {
  const [isToggling, setIsToggling] = useState(false);
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);

  const entries = Object.entries(productSummary);

  const additionalCover = Object.fromEntries(
    entries.filter(([, value]) => value.optional),
  ) as Record<string, ProductProps>;

  const product = Object.fromEntries(
    entries.filter(([, value]) => !value.optional),
  ) as Record<string, ProductProps>;

  const toggleItem = async (itemRef: string | undefined) => {
    setIsToggling(true);
    try {
      const url = `?step_action=update_cart_item`;
      const payload = JSON.stringify({
        item_id: itemRef,
        action: "update",
      });
      /* eslint-disable @typescript-eslint/no-unsafe-assignment -- fetchRequest returns untyped JSON */
      const response: CartUpdateResponse = await fetchRequest(url, payload);
      onUpdateItem(response, itemRef);
      /* eslint-enable @typescript-eslint/no-unsafe-assignment */
    } catch (error) {
      console.error("Failed to update cart item:", error);
    } finally {
      setIsToggling(false);
    }
  };

  const showToggleButton =
    eligibleForRemoval && !promptInformation.promptCustomer;
  const toggleIcon = markedForPurchase ? bin : plus;
  const toggleLabel = markedForPurchase ? "Remove" : "Add";
  const toggleTitle = markedForPurchase
    ? "Remove product from cart"
    : "Add product to cart";

  return (
    <Segment>
      <Flex className="mobius-summary-wrapper__segment">
        <Image
          className="mobius-summary-wrapper__image"
          src={insurerLogo}
          alt="Insurer logo"
        />
        {!eligibleForRemoval &&
          !promptInformation.promptCustomer &&
          editURL && (
            <DialogueModal
              cancelButtonText="Cancel, return to checkout"
              headerText="Editing your coverage"
              actionButtonText="Go back to your quote selection"
              redirectFunction={() => (window.location.href = editURL)}
              description={editModalDescription || ""}
              button={
                <Button
                  className="mobius-summary-wrapper__button"
                  variant="primary"
                  size="sm"
                >
                  {isCombinedPaymentPage ? (
                    "Edit"
                  ) : (
                    <span className="mobius-summary-wrapper__button-content">
                      <Icon icon={pencil} title="Edit product details" />
                      Edit
                    </span>
                  )}
                </Button>
              }
            />
          )}
        {showToggleButton && (
          <Button
            className="mobius-summary-wrapper__button"
            variant="primary"
            size="sm"
            isLoading={isToggling}
            isDisabled={isToggling}
            onPress={() => {
              void toggleItem(cartItemReference);
            }}
          >
            <span className="mobius-summary-wrapper__button-content">
              <Icon icon={toggleIcon} title={toggleTitle} />
              {toggleLabel}
            </span>
          </Button>
        )}
      </Flex>
      <div
        data-testid="summary-wrapper-content"
        className={classNames("mobius-summary-wrapper__content", {
          "mobius-summary-wrapper__content--excluded": !markedForPurchase,
        })}
      >
        <ProductSummary
          productName={productName}
          product={product}
          additionalCover={additionalCover}
          totalGrossPremium={totalGrossPremium}
          hasSharedLimits={hasSharedLimits}
          mobileView={mobileView}
          policyStartDate={policyStartDate}
          applicationDashboard={applicationDashboard}
          updateApplicationDashboard={updateApplicationDashboard}
          isOnlyProduct={isOnlyProduct}
          hasPrimaryCover={hasPrimaryCover}
        />
      </div>
    </Segment>
  );
};
